import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './RegexTester.module.css';

type FlagKey = 'g' | 'i' | 'm' | 's' | 'u' | 'y';

const escapeHtml = (text: string): string =>
  text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const flagOptions: Array<{ key: FlagKey; labelKey: string }> = [
  { key: 'g', labelKey: 'regexTester.flagLabels.g' },
  { key: 'i', labelKey: 'regexTester.flagLabels.i' },
  { key: 'm', labelKey: 'regexTester.flagLabels.m' },
  { key: 's', labelKey: 'regexTester.flagLabels.s' },
  { key: 'u', labelKey: 'regexTester.flagLabels.u' },
  { key: 'y', labelKey: 'regexTester.flagLabels.y' }
];

const RegexTester: React.FC = () => {
  const { t } = useTranslation();
  const [pattern, setPattern] = useState('\\b(\\w+)@(\\w+\\.\\w+)\\b');
  const [sourceText, setSourceText] = useState('test@example.com\nfoo@bar.com\ninvalid-email');
  const [flags, setFlags] = useState<Record<FlagKey, boolean>>({
    g: true,
    i: false,
    m: false,
    s: false,
    u: false,
    y: false
  });

  const enabledFlags = useMemo(
    () =>
      (Object.keys(flags) as FlagKey[])
        .filter((f) => flags[f])
        .join(''),
    [flags]
  );

  const analysis = useMemo(() => {
    if (!pattern) {
      return {
        error: '',
        highlightedHtml: escapeHtml(sourceText),
        matches: [] as Array<{
          index: number;
          text: string;
          groups: string[];
        }>
      };
    }

    try {
      const regex = new RegExp(pattern, enabledFlags || undefined);
      const runtimeRegex = flags.g ? regex : new RegExp(pattern, `${enabledFlags}g`);
      const rawMatches = Array.from(sourceText.matchAll(runtimeRegex));

      const matches = rawMatches.map((m) => ({
        index: m.index ?? 0,
        text: m[0] ?? '',
        groups: (m.slice(1) as Array<string | undefined>).map((v) => v ?? '')
      }));

      let highlightedHtml = '';
      let lastIndex = 0;
      matches.forEach((m) => {
        const start = m.index;
        const end = start + m.text.length;
        highlightedHtml += escapeHtml(sourceText.slice(lastIndex, start));
        highlightedHtml += `<mark class="${styles.mark}">${escapeHtml(m.text)}</mark>`;
        lastIndex = end;
      });
      highlightedHtml += escapeHtml(sourceText.slice(lastIndex));

      return { error: '', highlightedHtml, matches };
    } catch (e) {
      return {
        error: e instanceof Error ? e.message : t('regexTester.expressionErrorFallback'),
        highlightedHtml: escapeHtml(sourceText),
        matches: [] as Array<{ index: number; text: string; groups: string[] }>
      };
    }
  }, [pattern, sourceText, enabledFlags, flags.g, t]);

  const toggleFlag = (flag: FlagKey) => {
    setFlags((prev) => ({ ...prev, [flag]: !prev[flag] }));
  };

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <h2 className={styles.title}>{t('regexTester.title')}</h2>
        <p className={styles.description}>{t('regexTester.description')}</p>

        <div className={styles.label}>{t('regexTester.labelRegex')}</div>
        <input
          value={pattern}
          onChange={(e) => setPattern(e.target.value)}
          className={styles.input}
          placeholder={t('regexTester.regexPlaceholder')}
        />

        <div className={styles.flagsRow}>
          {flagOptions.map((item) => (
            <label key={item.key} className={styles.flagItem}>
              <input
                type="checkbox"
                checked={flags[item.key]}
                onChange={() => toggleFlag(item.key)}
              />
              <span>{t(item.labelKey)}</span>
            </label>
          ))}
        </div>

        <div className={styles.label}>{t('regexTester.testTextLabel')}</div>
        <textarea
          value={sourceText}
          onChange={(e) => setSourceText(e.target.value)}
          className={styles.textarea}
          placeholder={t('regexTester.testTextPlaceholder')}
        />

        {analysis.error ? (
          <div className={`${styles.tip} ${styles.error}`}>{t('regexTester.tipErrorPrefix')}
            {analysis.error}
          </div>
        ) : (
          <div className={`${styles.tip} ${styles.success}`}>{t('regexTester.tipMatch', { count: analysis.matches.length })}</div>
        )}

        <div className={styles.label}>{t('regexTester.labelHighlighted')}</div>
        <pre
          className={styles.preview}
          dangerouslySetInnerHTML={{ __html: analysis.highlightedHtml || '&nbsp;' }}
        />

        <div className={styles.label}>{t('regexTester.labelGroups')}</div>
        <div className={styles.groupsBox}>
          {analysis.matches.length === 0 ? (
            <div className={styles.groupEmpty}>{t('regexTester.groupEmpty')}</div>
          ) : (
            analysis.matches.map((m, idx) => (
              <div key={`${idx}-${m.index}`} className={styles.groupItem}>
                <div>{t('regexTester.matchLine', { n: idx + 1, text: m.text, pos: m.index })}</div>
                {m.groups.length > 0 ? (
                  m.groups.map((g, gIdx) => (
                    <div key={`${idx}-${gIdx}`}>{t('regexTester.groupLine', { gIdx: gIdx + 1, value: g })}</div>
                  ))
                ) : (
                  <div>{t('regexTester.noCaptureGroups')}</div>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default RegexTester;
